import { useEffect, useState } from 'react';
import {
  BoxV2 as Box,
  TextInput,
  Button,
  MultimediaList,
  AttachmentPreview,
  AttachmentInfo,
} from 'portal-commons';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
} from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';

import { isValidInput } from '../../../../../utils';
import SampleMultimediaUploader from '../SampleMultimediaUploader';
import {
  deleteAttachment,
  downloadAttachmentToDesktop,
  uploadAttachment,
} from '../../apiServices';
import { AppealCategoryData, AppealRequestData } from '../../types';
import AppealCategory from '../AppealCategory';

interface RcsVetAppealModalProps {
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (data: AppealRequestData) => void;
  price?: number | string;
  waitingTime?: string;
  appealCategories: AppealCategoryData[];
  disabledCategoryIds?: string[];
}

const useStyles = makeStyles(() => ({
  container: {
    width: '700px',
    maxWidth: 'unset',
    padding: '35px 30px 25px 30px',
    marginLeft: '200px',
    boxSizing: 'border-box',
    lineHeight: 'normal',
  },
  title: {
    padding: '0',
    '& > *': {
      textAlign: 'center',
      fontWeight: 500,
      fontSize: '20px',
      lineHeight: '23px',
      color: '#071822',
    },
  },
  dialogContentRoot: {
    color: '#333E41',
  },
}));

const ATTACHMENT_LIMIT = 10;
const RcsVetAppealModal: React.FC<RcsVetAppealModalProps> = ({
  open,
  submitting,
  onClose,
  onSubmit,
  price,
  waitingTime,
  appealCategories = [],
  disabledCategoryIds = [],
}) => {
  const classes = useStyles();
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [attachmentPreviewIndex, setAttachmentPreviewIndex] = useState(-1);
  const {
    control,
    reset: resetFormFields,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      explanation: '',
      categories: [],
    },
  });
  const displayPrice = price == null ? '--' : `$${price}`;

  useEffect(() => {
    if (!open) {
      resetStates();
    }
  }, [open]);

  const submitForm = (data: Omit<AppealRequestData, 'attachments'>) => {
    onSubmit({
      ...data,
      attachments: attachments.map((attachment) => attachment.uuid),
    });
  };

  const handleDownload = async (index: number) => {
    try {
      await downloadAttachmentToDesktop(
        attachments[index].uuid,
        attachments[index].fileName
      );
    } catch (e) {
      // ignore
    }
  };

  const handleUpload = async (file: File) => {
    setLoading(true);
    const originalAttachments = attachments.slice();
    try {
      const attachment: AttachmentInfo = await uploadAttachment(file);
      setAttachments([
        ...attachments,
        {
          ...attachment,
          file,
        },
      ]);
    } catch (e) {
      setAttachments(originalAttachments);
    }
    setLoading(false);
  };

  const handlePreview = async (index: number) => {
    setAttachmentPreviewIndex(index);
  };

  const handleDelete = async (index: number) => {
    const removedAttachment = attachments[index];
    const newAttachments = attachments.filter((_, idx) => idx !== index);
    const originalAttachments = attachments.slice();
    setAttachments(newAttachments);
    try {
      await deleteAttachment(removedAttachment.uuid);
    } catch (e) {
      setAttachments(originalAttachments);
    }
  };

  const resetStates = () => {
    resetFormFields();
    setLoading(false);
    setAttachments([]);
    setAttachmentPreviewIndex(-1);
  };

  const handleModalClose = () => {
    onClose?.();
    resetStates();
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      data-testid="appealRequestModal"
      classes={{
        paper: classes.container,
      }}
    >
      <DialogTitle
        classes={{
          root: classes.title,
        }}
        data-testid="appealRequestModalTitle"
      >
        RCS Vet Appeal
      </DialogTitle>
      <form noValidate onSubmit={handleSubmit(submitForm)}>
        <DialogContent
          data-testid="appealRequestModalContent"
          classes={{ root: classes.dialogContentRoot }}
        >
          <Box as="p" sx={{ mt: 'l', mb: 'm' }}>
            You can submit an Appeal up to 45 days after the completion of a
            vet. Appeals are charged {displayPrice} regardless of the outcome.
          </Box>
          <Box as="h3" sx={{ fontWeight: 'bold', fontSize: 'H500', mb: 'xxs' }}>
            Appeal Categories
          </Box>
          <Box
            as="p"
            sx={{
              mb: 'm',
              color: errors.categories?.message ? 't.paleRed600' : undefined,
            }}
          >
            You are required to select at least one category, but may select
            more
          </Box>
          <Controller
            name="categories"
            control={control}
            rules={{
              validate: {
                required: (value) =>
                  value.length > 0 || 'At least one category is required',
              },
            }}
            render={({ field: { value, onChange } }) => {
              return (
                <>
                  {appealCategories.map((category) => (
                    <Box sx={{ mb: 's' }} key={category.appealCategoryId}>
                      <AppealCategory
                        disabled={disabledCategoryIds.includes(
                          category.appealCategoryId
                        )}
                        checked={(value as string[]).includes(
                          category.appealCategoryId
                        )}
                        title={category.displayName}
                        message={category.description}
                        onValueChange={(checked) => {
                          const categoryId = category.appealCategoryId;
                          const newValue = checked
                            ? [...value, categoryId]
                            : value.filter((id) => id !== categoryId);
                          onChange(newValue);
                        }}
                        contentColor="#333E41"
                      />
                    </Box>
                  ))}
                </>
              );
            }}
          />

          <Box as="small" sx={{ color: 't.grey500', fontSize: 'H100' }}>
            * Indicates a Required Field
          </Box>
          <Box sx={{ mt: 's', mb: 's' }}>
            <Controller
              name="explanation"
              control={control}
              rules={{
                validate: {
                  maxLength: (value) =>
                    value.length <= 1024 || 'Maximum 1024 chars are allowed',
                  patter: (val) => isValidInput(val) || 'Invalid input',
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error = {} },
              }) => (
                <TextInput
                  data-testid="appealRequestModalExplanationInput"
                  label="Explanation"
                  value={value}
                  onChange={onChange}
                  error={!!error.message}
                  note={error.message}
                  maxLength={1024}
                />
              )}
            />
          </Box>
          <SampleMultimediaUploader
            loading={loading}
            onChange={handleUpload}
            disabled={attachments.length >= ATTACHMENT_LIMIT}
            max={ATTACHMENT_LIMIT}
            data-testid="appealRequestModalFileUploader"
          />
          <Box sx={{ mt: 's', mb: 'm' }}>
            <MultimediaList
              title="Sample Multimedia Files"
              emptyMessage="No files have been attached"
              editable
              attachments={attachments}
              onDownload={handleDownload}
              onSelect={handlePreview}
              onDelete={handleDelete}
              loading={
                attachmentPreviewIndex > -1 &&
                !attachments[attachmentPreviewIndex].file
              }
              data-testid="appealRequestSampleMultiMediaList"
              style={{ minHeight: 160 }}
            />
          </Box>
          <Box
            sx={{
              color: 't.blue120',
              fontSize: '16px',
              textAlign: 'center',
              lineHeight: 'normal',
              fontWeight: 'semiBold',
            }}
          >
            <div>Vetting Price: {displayPrice}</div>
            <div>
              Estimated Time for Vetting: {!waitingTime ? '--' : waitingTime}
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              gap: 'l',
            }}
          >
            <Button
              disabled={loading || submitting}
              variant="outline"
              size="medium"
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              disabled={loading || submitting}
              type="submit"
              variant="standard"
              size="medium"
            >
              Submit
            </Button>
          </Box>
        </DialogActions>
      </form>

      {attachmentPreviewIndex > -1 &&
        attachments[attachmentPreviewIndex].file && (
          <AttachmentPreview
            onClose={() => setAttachmentPreviewIndex(-1)}
            file={attachments[attachmentPreviewIndex].file!}
            mimeType={attachments[attachmentPreviewIndex].mimeType}
            data-testid="appealRequestModalBackdrop"
          />
        )}
    </Dialog>
  );
};

export default RcsVetAppealModal;
